import { slugify } from "@/lib/events";
import type { MediaType, TrailerOption } from "@/lib/api";
import { DEFAULT_LOCALE, LOCALE_META, type Locale } from "@/lib/i18n/config";
import { getMessages } from "@/lib/i18n/messages";
import messages from "./movies.i18n";

// URL con id + slug del título (ej. "603-matrix") — el id es lo único que
// se usa para pedir datos al backend, el slug es solo para que la URL sea
// legible/indexable. No se valida que coincidan: si alguien cambia el texto
// a mano, igual resuelve por id (mismo criterio "no reversible" de slugify).
// Sin ?region: una sola URL por película para que la página se pueda cachear
// (la región elegida vive en localStorage, ver lib/detect-region.ts).
// Las series viven en /viva-cine/serie/<id>-<slug>: TMDB numera películas
// y series por separado, así que el id solo no basta para saber qué es.
export function movieHref(movie: { id: number; title: string; mediaType?: MediaType }): string {
  const base = movie.mediaType === "tv" ? "/viva-cine/serie" : "/viva-cine";
  return `${base}/${movie.id}-${slugify(movie.title)}`;
}

// key de React para listas mezcladas (película 1399 ≠ serie 1399).
export function titleKey(item: { id: number; mediaType?: MediaType }): string {
  return `${item.mediaType ?? "movie"}-${item.id}`;
}

export function formatSeasons(seasons?: number, episodes?: number, lang: Locale = DEFAULT_LOCALE): string | null {
  if (!seasons) return null;
  const t = getMessages(messages, lang);
  return episodes ? `${t.seasons(seasons)} · ${t.episodes(episodes)}` : t.seasons(seasons);
}

// "2026-10-12" → "12 de octubre de 2026" (fecha calendario, sin zona horaria).
export function formatAirDate(date: string, lang: Locale = DEFAULT_LOCALE): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(LOCALE_META[lang].intl, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function parseMovieId(slugParam: string): number | null {
  const match = /^(\d+)/.exec(slugParam);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
}

// El link de un provider es la búsqueda en su plataforma o, si la
// plataforma no tiene búsqueda por URL, la página "dónde ver" de TMDB.
export function providerLinkLabel(
  provider: { providerName: string; url?: string },
  lang: Locale = DEFAULT_LOCALE,
): string {
  const t = getMessages(messages, lang);
  return provider.url?.includes("themoviedb.org") ? t.whereToWatch : t.searchOn(provider.providerName);
}

export function formatRuntime(minutes?: number): string | null {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Qué tan bien le sirve un trailer a quien mira (mayor = mejor; 0 = no
// sirve). En español el doblaje de España no es lo mismo que el latino: con
// España elegida en el selector de país se prefiere el de España; con
// cualquier otro país, el latino (y el de España queda al final, después del
// original en inglés). En inglés u otro idioma: ese idioma y luego inglés.
function trailerRank(trailer: TrailerOption, lang: Locale, country: string): number {
  if (lang === "es" && trailer.language === "es") {
    const fromSpain = trailer.country === "ES";
    if (country === "ES") return fromSpain ? 3 : 2;
    return fromSpain ? 1 : 3;
  }
  if (trailer.language === lang) return 3;
  return trailer.language === "en" ? 2 : 0;
}

// El trailer a mostrar. Dentro de un mismo idioma se respeta el orden del
// backend (trailer antes que teaser, oficiales, el más reciente).
export function pickTrailer(
  trailers: TrailerOption[] | undefined,
  lang: Locale,
  country: string,
): TrailerOption | null {
  let best: TrailerOption | null = null;
  let bestRank = 0;
  for (const trailer of trailers ?? []) {
    const rank = trailerRank(trailer, lang, country);
    if (rank > bestRank) {
      best = trailer;
      bestRank = rank;
    }
  }
  return best;
}
